import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { languages, LanguageCode } from '../../i18n/languages';

interface MobileLanguageSelectorProps {
  onClose?: () => void;
}

export default function MobileLanguageSelector({ onClose }: MobileLanguageSelectorProps) {
  const { i18n, t } = useTranslation('mobile');
  const [isOpen, setIsOpen] = useState(false);

  const currentLang = languages[i18n.language as LanguageCode] || languages.en;
  const currentCode = (i18n.language as LanguageCode) || 'en';

  const handleLanguageChange = async (langCode: LanguageCode) => {
    await i18n.changeLanguage(langCode);
    localStorage.setItem('i18nextLng', langCode);
    setIsOpen(false);
    onClose?.();
  };

  // Group languages by RTL/LTR
  const ltrLanguages = Object.entries(languages).filter(([_, lang]) => lang.dir === 'ltr');
  const rtlLanguages = Object.entries(languages).filter(([_, lang]) => lang.dir === 'rtl');

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl transition-colors touch-manipulation"
      >
        <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl flex items-center justify-center">
          <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
          </svg>
        </div>
        <div className="flex-1 text-left">
          <p className="font-medium text-gray-900 dark:text-white">{t('menu.language')}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {currentLang.nativeName}
          </p>
        </div>
        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    );
  }

  return (
    <div className="animate-fadeIn">
      <div className="flex items-center gap-3 p-4 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setIsOpen(false)}
          className="p-2 -ml-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg touch-manipulation"
          aria-label={t('common.back')}
        >
          <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h3 className="font-semibold text-gray-900 dark:text-white">{t('menu.changeLanguage')}</h3>
      </div>

      <div className="max-h-80 overflow-y-auto">
        {/* LTR Languages */}
        <div className="p-2">
          {ltrLanguages.map(([code, lang]) => (
            <button
              key={code}
              onClick={() => handleLanguageChange(code as LanguageCode)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors touch-manipulation ${
                currentCode === code
                  ? 'bg-primary-50 dark:bg-primary-900/20'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <span className="flex-1">
                <span className="font-medium text-gray-900 dark:text-white">{lang.nativeName}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400 block">{lang.name}</span>
              </span>
              {currentCode === code && (
                <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
        </div>

        {/* RTL Languages */}
        {rtlLanguages.length > 0 && (
          <>
            <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase flex items-center gap-2">
              <span className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded">
                RTL
              </span>
            </div>
            <div className="p-2">
              {rtlLanguages.map(([code, lang]) => (
                <button
                  key={code}
                  onClick={() => handleLanguageChange(code as LanguageCode)}
                  dir="rtl"
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-right transition-colors touch-manipulation ${
                    currentCode === code
                      ? 'bg-primary-50 dark:bg-primary-900/20'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className="flex-1">
                    <span className="font-medium text-gray-900 dark:text-white">{lang.nativeName}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 block">{lang.name}</span>
                  </span>
                  {currentCode === code && (
                    <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
